"use client";

import { clsx } from "@trydig/ui";
import { type ComponentProps, Fragment, type ReactNode } from "react";
import { useTranslations } from "@/i18n/client";
import { Link } from "@/i18n/routing";
import { Skeleton } from "../Skeleton";
import styles from "./Breadcrumbs.module.css";

interface Breadcrumb {
	name: string | ReactNode;
	href?: string;
	onClick?: () => void;
	uri?: string;
}

export interface BreadcrumbsProps extends ComponentProps<"div"> {
	breadcrumbs?: Breadcrumb[];
	last?: Breadcrumb;
}

const capitalizeFirstLetterOfEachWord = (element: Breadcrumb["name"]) => {
	if (typeof element === "string") {
		return element
			.replace(/\w\S*/g, (word) => {
				return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
			})
			.replaceAll("-", " ");
	}
	return element;
};

export const Breadcrumbs = ({
	breadcrumbs,
	className,
	last,
	...rest
}: BreadcrumbsProps) => {
	const reducedBreadcrumbs = breadcrumbs?.map((breadcrumb, index) => ({
		...breadcrumb,
		name: capitalizeFirstLetterOfEachWord(breadcrumb.name),
		...(index === breadcrumbs.length - 1 ? last : {}),
	}));

	const t = useTranslations();

	if ((reducedBreadcrumbs?.length || 0) <= 1) return null;

	return (
		<nav
			className={clsx(styles.root, className)}
			{...rest}
			aria-label="Breadcrumb"
		>
			<ol>
				{reducedBreadcrumbs?.map((breadcrumb, i) => {
					const translatedName =
						typeof breadcrumb.name === "string"
							? t(breadcrumb.name)
							: breadcrumb.name;

					const slash =
						i !== reducedBreadcrumbs.length - 1 ? (
							<li>
								<span className={styles.breadcrumb}>/</span>
							</li>
						) : undefined;

					if (
						(!breadcrumb.href && !breadcrumb.uri) ||
						i === reducedBreadcrumbs.length - 1
					) {
						return (
							<Fragment key={i}>
								<li>
									<button
										type="button"
										onClick={breadcrumb.onClick}
										className={styles.breadcrumb}
										key={i}
									>
										{translatedName}
									</button>
								</li>
								{slash}
							</Fragment>
						);
					}
					return (
						<Fragment key={i}>
							<li key={i}>
								<Link
									href={breadcrumb.href ?? breadcrumb.uri ?? ""}
									className={styles.breadcrumb}
									aria-label={`Navigate to ${typeof breadcrumb.name === "string" ? breadcrumb.name : "this section"}`}
								>
									{translatedName}
								</Link>
							</li>
							{slash}
						</Fragment>
					);
				})}
			</ol>
		</nav>
	);
};

export const BreadcrumbsSkeleton = ({
	size = 5,
	className,
}: {
	size?: number;
	className?: string;
}) => {
	return (
		<nav
			aria-busy
			className={clsx(styles.root, className)}
			aria-label="Breadcrumb"
		>
			{[...Array(size)].map((_, idx) => (
				<Skeleton fitContent key={idx}>
					<div className={styles.breadcrumb}>{idx % 2 ? "/" : "Crumb"}</div>
				</Skeleton>
			))}
		</nav>
	);
};
